import PropTypes from 'prop-types';
import {
  Box,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Flex,
  Button,
  Text,
  Image,
  Center,
  useColorModeValue,
} from '@chakra-ui/react';

import { AttachmentIcon, CloseIcon } from '@chakra-ui/icons';
import { MdInfo } from 'react-icons/md';

function ImageUploadInput({
  label,
  helperText,
  currentImage,
  file,
  fileURL,
  fileError,
  fileInputRef,
  handleRemoveSelectedFile,
  handleAttachFile,
  handleFileInputChange,
  hideImagePreview = false,
  children,
}) {
  const baseFileUrl = process.env.REACT_APP_FILE_URL;

  const selectedFileBg = useColorModeValue('gray.50', 'gray.700');
  const infoBlueBg = useColorModeValue('blue.50', 'blue.700');
  const infoTextColor = useColorModeValue('black', 'white');

  return (
    <FormControl isInvalid={fileError !== ''}>
      <FormLabel optionalIndicator>{label}</FormLabel>

      {/* Custom children content (e.g., CategoryImage preview) */}
      {children}

      {!hideImagePreview && !file.name && currentImage && (
        <Box my={4}>
          <Center>
            <Box aspectRatio={16 / 9}>
              <Image
                borderRadius="10"
                width="100%"
                height="100%"
                objectFit="cover"
                fallbackSrc={require('assets/default-course.jpg')}
                src={file?.name ? fileURL : `${baseFileUrl}${currentImage}`}
                alt="Course current image"
              />
            </Box>
          </Center>

          <Center mt={2}>
            <Text fontSize="xs" fontWeight={800}>
              {file?.name ? 'NEW' : 'CURRENT'} {label.toUpperCase()}
            </Text>
          </Center>
        </Box>
      )}

      {!hideImagePreview && file?.name && (
        <Box my={4}>
          <Center>
            <Box aspectRatio={16 / 9}>
              <Image
                borderRadius="10"
                width="100%"
                height="100%"
                objectFit="cover"
                fallbackSrc={require('assets/default-course.jpg')}
                src={fileURL}
                alt="Selected File"
              />
            </Box>
          </Center>

          <Box
            p={3}
            bg={infoBlueBg}
            borderRadius="md"
            border="1px solid"
            borderColor="blue.200"
          >
            <Flex gap={2}>
              <MdInfo fontSize="1.2em" color="#58a4d6ff" />
              <Text fontSize="xs" color={infoTextColor}>
                <strong>Info:</strong> The exact image shown above will be used
                as the course thumbnail once you save your changes.
              </Text>
            </Flex>
          </Box>
        </Box>
      )}

      {/* File management - always show when file is selected */}
      {file?.name && (
        <Flex
          gap={2}
          my={2}
          px={3}
          justify="center"
          align="center"
          bg={selectedFileBg}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
        >
          <Text fontSize="xs" flex={1}>
            <strong>Selected File:</strong> {file?.name}
          </Text>
          <Button
            type="button"
            colorScheme="teal"
            variant="ghost"
            onClick={handleRemoveSelectedFile}
          >
            <CloseIcon />
          </Button>
        </Flex>
      )}
      <Box
        as="button"
        type="button"
        borderRadius="md"
        bg="teal"
        w="100%"
        px={4}
        h={10}
        color="white"
        onClick={handleAttachFile}
      >
        <input
          type="file"
          hidden
          accept={'image/*'}
          ref={fileInputRef}
          onChange={handleFileInputChange}
        />
        <AttachmentIcon mx={2} />
        Attach file
      </Box>
      {fileError !== '' && <FormErrorMessage>{fileError}</FormErrorMessage>}
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
}

ImageUploadInput.defaultProps = {
  currentImage: null,
  fileURL: null,
  hideImagePreview: false,
  children: null,
};

ImageUploadInput.propTypes = {
  currentImage: PropTypes.string,
  label: PropTypes.string.isRequired,
  helperText: PropTypes.string.isRequired,
  file: PropTypes.object.isRequired,
  fileURL: PropTypes.string,
  fileError: PropTypes.string.isRequired,
  fileInputRef: PropTypes.object.isRequired,
  handleRemoveSelectedFile: PropTypes.func.isRequired,
  handleAttachFile: PropTypes.func.isRequired,
  handleFileInputChange: PropTypes.func.isRequired,
  hideImagePreview: PropTypes.bool,
  children: PropTypes.node,
};

export default ImageUploadInput;
